import { useEffect, useMemo, useState } from 'react';
import { Boxes, DollarSign, Leaf, Link, Scale, X } from 'lucide-react';
import { numberValue } from '../lib/pricing';

const MATERIAL_TYPES = ['PLA', 'PETG', 'ABS', 'ASA', 'TPU', 'Nylon', 'PC', 'PVA', 'HIPS', 'Resin', 'Other'];

function startingValues(material) {
  return {
    name: material?.name || '',
    brand: material?.brand || '',
    materialType: material?.materialType || '',
    color: material?.color || '',
    diameterMm: material?.diameterMm ?? '',
    spoolWeightGrams: material?.spoolWeightGrams ?? '',
    remainingGrams: material?.remainingGrams ?? '',
    spoolPrice: material?.spoolPrice ?? '',
    lowStockThresholdGrams: material?.lowStockThresholdGrams ?? '',
    currency: material?.currency || 'USD',
    productUrl: material?.productUrl || '',
    linkedProfileId: material?.linkedProfileId || '',
  };
}

export default function MaterialForm({ material, profiles, saving, onClose, onSave }) {
  const [values, setValues] = useState(() => startingValues(material));
  const [error, setError] = useState('');
  const isEditing = Boolean(material?.id);
  const remainingPercent = useMemo(() => {
    const weight = numberValue(values.spoolWeightGrams);
    return weight > 0 ? Math.min(100, (numberValue(values.remainingGrams) / weight) * 100) : 0;
  }, [values.remainingGrams, values.spoolWeightGrams]);
  const remainingValue = useMemo(() => {
    const weight = numberValue(values.spoolWeightGrams);
    return weight > 0 ? numberValue(values.remainingGrams) * (numberValue(values.spoolPrice) / weight) : 0;
  }, [values.remainingGrams, values.spoolPrice, values.spoolWeightGrams]);
  const hasStockValues = values.spoolWeightGrams !== '' && values.remainingGrams !== '';

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, saving]);

  const update = (field) => (event) => setValues((current) => ({ ...current, [field]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setError('');
    const spoolWeight = numberValue(values.spoolWeightGrams);
    const remaining = numberValue(values.remainingGrams);
    if (!values.name.trim()) return setError('Give this material or spool a name.');
    if (!values.materialType) return setError('Choose a material type.');
    if (spoolWeight <= 0) return setError('Spool weight must be greater than zero.');
    if (remaining < 0 || remaining > spoolWeight) return setError('Remaining weight must be between zero and the full spool weight.');

    try {
      await onSave({
        name: values.name.trim(),
        brand: values.brand.trim(),
        materialType: values.materialType,
        color: values.color.trim(),
        diameterMm: Math.max(0, numberValue(values.diameterMm)),
        spoolWeightGrams: spoolWeight,
        remainingGrams: remaining,
        spoolPrice: Math.max(0, numberValue(values.spoolPrice)),
        lowStockThresholdGrams: Math.max(0, numberValue(values.lowStockThresholdGrams)),
        currency: values.currency.trim().toUpperCase() || 'USD',
        productUrl: values.productUrl.trim(),
        linkedProfileId: values.linkedProfileId,
      });
    } catch (saveError) {
      setError(saveError?.message || 'The material could not be saved.');
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <section className="profile-modal entity-modal" role="dialog" aria-modal="true" aria-labelledby="material-modal-title">
        <header className="profile-modal__header">
          <div><p className="eyebrow">Material inventory</p><h2 id="material-modal-title">{isEditing ? 'Update filament inventory' : 'Add filament inventory'}</h2><p>Track what is on the shelf, what it cost, and when a spool is running low.</p></div>
          <button className="icon-button" type="button" onClick={onClose} disabled={saving} aria-label="Close material form"><X size={20} /></button>
        </header>

        <form className="profile-form entity-form" onSubmit={submit}>
          <fieldset>
            <legend><Leaf size={17} /> Material identity</legend>
            <div className="entity-form__grid">
              <label className="field"><span className="field__heading">Inventory name</span><span className="field__control"><input autoFocus value={values.name} onChange={update('name')} placeholder="Name this spool or material" /></span></label>
              <label className="field"><span className="field__heading">Brand</span><span className="field__control"><input value={values.brand} onChange={update('brand')} placeholder="Enter the manufacturer" /></span></label>
              <label className="field"><span className="field__heading">Material type</span><span className="field__control field__control--select"><select value={values.materialType} onChange={update('materialType')}><option value="">Select material</option>{MATERIAL_TYPES.map((type) => <option value={type} key={type}>{type}</option>)}</select></span></label>
              <label className="field"><span className="field__heading">Color</span><span className="field__control"><input value={values.color} onChange={update('color')} placeholder="Enter the color" /></span></label>
              <label className="field"><span className="field__heading">Diameter</span><span className="field__control unit-field"><input type="number" min="0" step="0.01" value={values.diameterMm} onChange={update('diameterMm')} /><b>mm</b></span></label>
              <label className="field"><span className="field__heading">Linked cost profile <small>Optional</small></span><span className="field__control field__control--select"><select value={values.linkedProfileId} onChange={update('linkedProfileId')}><option value="">Not linked</option>{profiles.map((profile) => <option value={profile.id} key={profile.id}>{profile.name}</option>)}</select></span></label>
            </div>
          </fieldset>

          <fieldset>
            <legend><Scale size={17} /> Spool and stock</legend>
            <div className="entity-form__grid">
              <label className="field"><span className="field__heading">Full spool weight</span><span className="field__control unit-field"><input type="number" min="1" step="1" value={values.spoolWeightGrams} onChange={update('spoolWeightGrams')} /><b>g</b></span></label>
              <label className="field"><span className="field__heading">Remaining filament</span><span className="field__control unit-field"><input type="number" min="0" step="1" value={values.remainingGrams} onChange={update('remainingGrams')} /><b>g</b></span></label>
              <label className="field"><span className="field__heading">Low-stock alert</span><span className="field__control unit-field"><input type="number" min="0" step="1" value={values.lowStockThresholdGrams} onChange={update('lowStockThresholdGrams')} /><b>g</b></span></label>
              <label className="field"><span className="field__heading">Purchase price</span><span className="field__control unit-field"><b>$</b><input type="number" min="0" step="0.01" value={values.spoolPrice} onChange={update('spoolPrice')} /></span></label>
              <label className="field"><span className="field__heading">Currency</span><span className="field__control"><input maxLength="3" value={values.currency} onChange={update('currency')} /></span></label>
              <label className="field"><span className="field__heading">Product URL <small>Optional</small></span><span className="field__control"><input type="url" value={values.productUrl} onChange={update('productUrl')} placeholder="https://…" /></span></label>
            </div>
          </fieldset>

          <aside className="entity-form__preview material-preview" aria-label="Material inventory preview">
            <span><Boxes size={21} /></span>
            <div><small>Remaining</small><strong>{hasStockValues ? `${remainingPercent.toFixed(0)}%` : 'Waiting for your values'}</strong></div>
            <div><small>Inventory value</small><strong>{hasStockValues && values.spoolPrice !== '' ? `$${remainingValue.toFixed(2)}` : 'Not calculated'}</strong></div>
            <div><small>Status</small><strong>{hasStockValues ? (numberValue(values.remainingGrams) <= numberValue(values.lowStockThresholdGrams) ? 'Low stock' : 'In stock') : 'Not configured'}</strong></div>
            {values.productUrl ? <Link size={18} /> : <DollarSign size={18} />}
          </aside>

          {error && <p className="form-status form-status--error" role="alert">{error}</p>}
          <footer className="profile-modal__actions"><button className="button button--ghost" type="button" onClick={onClose} disabled={saving}>Cancel</button><button className="button button--primary" type="submit" disabled={saving}>{saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add material'}</button></footer>
        </form>
      </section>
    </div>
  );
}
